/**
 * Offline Request Manager
 * Handles saving requests when offline and retrying them when online
 */

import { 
  saveRequest, 
  isOnline, 
  retryRequest, 
  getPendingRequests, 
  deleteRequest, 
  removeCustomerFromCache, 
  removeVendorFromCache, 
  removeTaskFromCache, 
  removeQuotationFromCache, 
  removeContractFromCache,
  saveGenericResponseCache,
  getGenericResponseCache
} from './offlineQueue';
import { BASE_URL, getToken, getCurrentLanguage } from './utils';
import { toast } from 'react-toastify';

/**
 * Register Background Sync if available
 */
export const registerBackgroundSync = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      // Check if sync is available on the registration object
      if (registration.sync) {
        await registration.sync.register('sync-pending-requests');
      }
    } catch (error) {
    }
  }
};

/**
 * Make a request with offline support
 * If the request fails due to network issues, it will be saved to IndexedDB
 * and retried automatically when network is available
 */
export const makeOfflineRequest = async (url, method, formData, additionalHeaders = {}) => {
  const token = getToken();
  const currentLang = getCurrentLanguage();

  // When sending FormData, don't set Content-Type - browser will set it automatically with boundary
  const headers = {
    Authorization: `Bearer ${token}`,
    'Accept-Language': currentLang,
    'Accept': 'application/json',
    ...additionalHeaders,
  };

  
  // Adjust Content-Type based on body type: for plain objects send JSON, for FormData let browser set boundary
  const isPlainObject = formData && typeof formData === 'object' && !(formData instanceof FormData);
  if (isPlainObject) {
    headers['Content-Type'] = 'application/json';
  } else if (headers['Content-Type']) {
    // Remove Content-Type for FormData so browser sets it correctly
    delete headers['Content-Type'];
  }

  // Check if we're offline first - if so, save immediately without trying to fetch
  const currentlyOnline = isOnline();

  
  if (!currentlyOnline) {
    
    // Proactive Cache Fallback for GET requests
    if (method === 'GET') {
      try {
        const cachedData = await getGenericResponseCache(url, {});
        if (cachedData) {
          return cachedData;
        }
      } catch (cacheError) {
        console.warn('[OfflineManager] Failed to retrieve cache:', cacheError);
      }
    }

    try {
      await saveRequest({
        url: `${BASE_URL}${url}`,
        method: method || 'POST',
        headers,
        formData,
      });
      // Register background sync for retry (non-blocking)
      registerBackgroundSync().catch(syncError => {
        console.warn('[OfflineManager] Background sync registration failed:', syncError);
      });
      
      const offlineError = new Error('OFFLINE_REQUEST_SAVED');
      offlineError.isOffline = true;
      throw offlineError;
    } catch (saveError) {
      if (saveError.message === 'OFFLINE_REQUEST_SAVED' || saveError.isOffline) {
        throw saveError;
      }
      console.error('[OfflineManager] Failed to save request:', saveError);
      const offlineError = new Error('OFFLINE_REQUEST_SAVED');
      offlineError.isOffline = true;
      throw offlineError;
    }
  }

  // If online, try to make the request
  try {
    const fetchOptions = {
      method: method || 'POST',
      headers,
    };
    
    // Only add body if provided (DELETE requests don't have body)
    if (formData) {
      fetchOptions.body = isPlainObject ? JSON.stringify(formData) : formData;
    }
    
    const response = await fetch(`${BASE_URL}${url}`, fetchOptions);

    if (!response.ok) {
      // Try to get error message from response
      let errorMessage = `HTTP error! status: ${response.status}`;
      let errorData = null;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
          // Extract message from response
          if (errorData?.message) {
            errorMessage = errorData.message;
          } else if (errorData?.data?.message) {
            errorMessage = errorData.data.message;
          } else if (errorData?.errors) {
            // Handle validation errors
            const errors = errorData.errors;
            if (typeof errors === 'object') {
              const errorMessages = Object.values(errors).flat();
              errorMessage = errorMessages.join(', ');
            }
          }
        }
      } catch (parseError) {
        // If parsing fails, use default message
        console.error('Error parsing error response:', parseError);
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.response = errorData;
      // Mark client errors (4xx) to prevent retry
      if (response.status >= 400 && response.status < 500) {
        error.isClientError = true;
      }
      throw error;
    }

    // DELETE requests might return empty response
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      
      // Save GET requests to cache
      if (method === 'GET') {
        saveGenericResponseCache(url, {}, data).catch(err => console.warn('Failed to save cache:', err));
      }
      
      return data;
    } else {
      return { success: true, message: 'Request completed successfully' };
    }
  } catch (error) {
    // Don't save if it's already an OFFLINE_REQUEST_SAVED error
    if (error.message === 'OFFLINE_REQUEST_SAVED' || error.isOffline) {
      throw error;
    }
    
    // Don't save client errors (4xx) - these won't succeed on retry
    if (error.isClientError || (error.status >= 400 && error.status < 500)) {
      throw error;
    }
    
    // Check if it's a network error (only for actual network failures, not HTTP errors)
    const isNetworkError = 
      error.message.includes('Failed to fetch') || 
      error.message.includes('NetworkError') ||
      error.message.includes('Network request failed') ||
      error.name === 'TypeError' ||
      (error.message && typeof error.message === 'string' && error.message.toLowerCase().includes('network'));
    
    // If request failed due to network error (not HTTP error), save it for retry
    if (isNetworkError) {
      try {
        await saveRequest({
          url: `${BASE_URL}${url}`,
          method: method || 'POST',
          headers,
          formData,
        });
        // Register background sync for retry
        await registerBackgroundSync();
        
        const offlineError = new Error('OFFLINE_REQUEST_SAVED');
        offlineError.isOffline = false; // Network error while online
        throw offlineError;
      } catch (saveError) {
        // If saving fails, still throw OFFLINE_REQUEST_SAVED to show user message
        if (saveError.message !== 'OFFLINE_REQUEST_SAVED' && !saveError.isOffline) {
          console.error('Error saving request to IndexedDB:', saveError);
        }
        const offlineError = new Error('OFFLINE_REQUEST_SAVED');
        offlineError.isOffline = false;
        throw offlineError;
      }
    }
    
    // Re-throw other errors (non-network, non-client errors)
    throw error;
  }
};

/**
 * Retry all pending requests
 */
export const retryAllPendingRequests = async () => {
  if (!isOnline()) {
    return { success: false, message: 'Still offline' };
  }

  try {
    const pendingRequests = await getPendingRequests();
    if (pendingRequests.length === 0) return { success: true, count: 0 };
    
    const results = [];

    for (const request of pendingRequests) {
      try {
        // Update headers with current token and language before retrying
        const currentToken = getToken();
        const currentLang = getCurrentLanguage();
        
        if (!currentToken) {
          results.push({ id: request.id, success: false, error: 'No authentication token' });
          continue;
        }
        
        const updatedRequest = {
          ...request,
          headers: {
            ...request.headers,
            Authorization: `Bearer ${currentToken}`,
            'Accept-Language': currentLang,
          },
        };
        
        const result = await retryRequest(updatedRequest);
        
        // If it's a DELETE request for a customer, remove from cache
        if (request.method === 'DELETE' && request.url.includes('/customers/')) {
          try {
            // Extract customer ID from URL
            const urlMatch = request.url.match(/\/customers\/(\d+)/);
            if (urlMatch && urlMatch[1]) {
              const customerId = parseInt(urlMatch[1], 10);
              await removeCustomerFromCache(customerId);
            }
          } catch (cacheError) {
            console.error('Error removing customer from cache after retry:', cacheError);
          }
        }
        
        // If it's a DELETE request for a vendor, remove from cache
        if (request.method === 'DELETE' && request.url.includes('/vendors/')) {
          try {
            // Extract vendor ID from URL
            const urlMatch = request.url.match(/\/vendors\/(\d+)/);
            if (urlMatch && urlMatch[1]) {
              const vendorId = parseInt(urlMatch[1], 10);
              await removeVendorFromCache(vendorId);
            }
          } catch (cacheError) {
            console.error('Error removing vendor from cache after retry:', cacheError);
          }
        }
        
        // If it's a DELETE request for a task, remove from cache
        if (request.method === 'DELETE' && request.url.includes('/tasks/')) {
          try {
            // Extract task ID from URL
            const urlMatch = request.url.match(/\/tasks\/(\d+)/);
            if (urlMatch && urlMatch[1]) {
              const taskId = parseInt(urlMatch[1], 10);
              await removeTaskFromCache(taskId);
            }
          } catch (cacheError) {
            console.error('Error removing task from cache after retry:', cacheError);
          }
        }

        // If it's a DELETE request for a quotation, remove from cache
        if (request.method === 'DELETE' && request.url.includes('/quotations/')) {
          try {
            // Extract quotation ID from URL
            const urlMatch = request.url.match(/\/quotations\/(\d+)/);
            if (urlMatch && urlMatch[1]) {
              const quotationId = parseInt(urlMatch[1], 10);
              await removeQuotationFromCache(quotationId);
            }
          } catch (cacheError) {
            console.error('Error removing quotation from cache after retry:', cacheError);
          }
        }
        

        // If it's a DELETE request for a contract, remove from cache
        if (request.method === 'DELETE' && request.url.includes('/contracts/')) {
          try {
            // Extract contract ID from URL
            const urlMatch = request.url.match(/\/contracts\/(\d+)/);
            if (urlMatch && urlMatch[1]) {
              const contractId = parseInt(urlMatch[1], 10);
              await removeContractFromCache(contractId);
            }
          } catch (cacheError) {
            console.error('Error removing contract from cache after retry:', cacheError);
          }
        }

        // If it's a PUT (update) request for a contract, clear all contracts cache so next fetch is fresh
        if (request.method === 'PUT' && request.url.includes('/contracts/')) {
          try {
            const { clearAllContractsCache } = await import('./offlineQueue');
            await clearAllContractsCache();
          } catch (cacheError) {
            console.error('Error clearing all contracts cache after contract update retry:', cacheError);
          }
        }

        // If it's a DELETE request for a penalty type, remove from cache
        if (request.method === 'DELETE' && request.url.includes('/penalty-types/')) {
          try {
            const urlMatch = request.url.match(/\/penalty-types\/(\d+)/);
            if (urlMatch && urlMatch[1]) {
              const penaltyTypeId = parseInt(urlMatch[1], 10);
              const { removePenaltyTypeFromCache } = await import('./offlineQueue');
              await removePenaltyTypeFromCache(penaltyTypeId);
            }
          } catch (cacheError) {
            console.error('Error removing penalty type from cache after retry:', cacheError);
          }
        }
        
        await deleteRequest(request.id);
        results.push({ 
          id: request.id, 
          success: true, 
          result,
          request: {
            method: request.method,
            url: request.url
          }
        });
      } catch (error) {
        console.error(`[OfflineManager] Failed to retry request ${request.id}:`, error);

        // CORS or "Failed to fetch" (TypeError) - remove from queue to prevent infinite retry loop
        const isCorsOrNetworkFailure =
          (error.name === 'TypeError' && (error.message === 'Failed to fetch' || (error.message && error.message.includes('fetch')))) ||
          (error.message && typeof error.message === 'string' && error.message.toLowerCase().includes('cors'));

        if (isCorsOrNetworkFailure) {
          console.warn(`CORS/network failure for request ${request.id}, removing from queue to prevent infinite retry`);
          try {
            await deleteRequest(request.id);
            results.push({
              id: request.id,
              success: false,
              error: error.message,
              removed: true,
              reason: 'CORS or network failure - will not retry',
            });
          } catch (deleteError) {
            console.error('Error deleting failed request:', deleteError);
            results.push({ id: request.id, success: false, error: error.message });
          }
          continue;
        }

        if (error.isClientError || (error.status >= 400 && error.status < 500)) {
          // If it's a 401 (Unauthorized), we should NOT delete it.
          // The user might just need to log in again.
          if (error.status === 401) {
            console.warn(`Unauthorized (401) for request ${request.id}, keeping in queue for after login`);
            results.push({ id: request.id, success: false, error: 'Unauthorized - keeping in queue' });
            continue;
          }

          console.warn(`Client error (${error.status}) for request ${request.id}, removing from queue to prevent infinite retry`);
          try {
            await deleteRequest(request.id);
            results.push({
              id: request.id,
              success: false,
              error: error.message,
              removed: true,
              reason: `Client error ${error.status} - will not retry`,
            });
          } catch (deleteError) {
            console.error('Error deleting failed request:', deleteError);
            results.push({ id: request.id, success: false, error: error.message });
          }
        } else {
          // Network or server errors - keep in queue for retry
          results.push({ id: request.id, success: false, error: error.message });
        }
      }
    }

    return {
      success: true,
      count: results.length,
      results,
    };
  } catch (error) {
    console.error('Error retrying pending requests', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check for pending requests and retry them
 * This should be called when the app starts or when network comes back online
 * Returns a promise that resolves when all retries are complete
 */
export const checkAndRetryPendingRequests = async () => {
  if (!isOnline()) {
    return { success: false, message: 'Still offline' };
  }

  // Always retry directly for faster execution
  // Service worker can be used as backup but direct retry is faster
  return await retryAllPendingRequests();
};

/**
 * Register online event listener to automatically retry requests
 * Emits a custom event when retries complete
 */
// Module-level lock to prevent race conditions across multiple calls/listeners
let isGlobalRetrying = false;

/**
 * Register online event listener to automatically retry requests
 * Emits a custom event when retries complete
 */
export const setupOfflineRequestListener = () => {
  let pollingInterval = null;
  let lastSyncTime = 0; // Track last sync to prevent duplicate toasts
  
  // Function to retry pending requests
  const performRetry = async () => {
    if (isGlobalRetrying) {
      return;
    }
    
    if (!isOnline()) {
      return;
    }
    
    
    // Check how many pending requests before retry
    const pendingBeforeRetry = await getPendingRequests();
    const countBefore = pendingBeforeRetry ? pendingBeforeRetry.length : 0;
    
    if (countBefore === 0) {
      return;
    }
    
    isGlobalRetrying = true;
    try {
      const result = await checkAndRetryPendingRequests();
      
      // Check how many pending requests after retry
      const pendingAfterRetry = await getPendingRequests();
      const countAfter = pendingAfterRetry ? pendingAfterRetry.length : 0;
      const actualSyncedCount = countBefore - countAfter;
      
      
      if (actualSyncedCount > 0) {
        const now = Date.now();
        // Only show toast if at least 2 seconds passed since last sync (prevent duplicates)
        if (now - lastSyncTime > 2000) {
          lastSyncTime = now;
          
          const currentLang = getCurrentLanguage();
          const message = currentLang === 'ar' 
            ? `تم مزامنة ${actualSyncedCount} عملية بنجاح`
            : `Successfully synced ${actualSyncedCount} operation${actualSyncedCount > 1 ? 's' : ''}`;
          
          toast.success(message, {
            toastId: `sync-success-${now}`,
            autoClose: 3000,
            rtl: currentLang === 'ar'
          });
          
          // Only dispatch event if there were successful retries
          if (result.results && result.results.length > 0) {
            const successfulRetries = result.results.filter(r => r.success === true);
            if (successfulRetries.length > 0) {
              window.dispatchEvent(new CustomEvent('offlineRequestsRetried', {
                detail: { 
                  count: actualSyncedCount,
                  results: result.results,
                  message: result.message
                }
              }));
            }
          }
        } else {
        }
      } else {
      }
    } catch (error) {
      console.error('[OfflineManager] Error in performRetry:', error);
    } finally {
      isGlobalRetrying = false;
    }
  };
  
  // Listen for online event
  window.addEventListener('online', async () => {
    
    // Retry immediately when online without delay
    // Try multiple times to handle race conditions
    await performRetry();
    
    // Second attempt after a small delay to ensure connection is stable
    setTimeout(async () => {
      await performRetry();
    }, 1000);
    
    // Also register background sync
    registerBackgroundSync();
  });
  
  // Listen for offline event
  window.addEventListener('offline', () => {
    // Event logged for debugging if needed
  });
  
  // Listen for visibility change (when user returns to tab)
  document.addEventListener('visibilitychange', async () => {
    if (!document.hidden && isOnline()) {
      await performRetry();
    }
  });
  
  // Listen for focus event (when window gets focus)
  window.addEventListener('focus', async () => {
    if (isOnline()) {
      setTimeout(async () => {
        await performRetry();
      }, 200);
    }
  });

  // Also check on page load
  if (isOnline()) {
    // Retry immediately on page load
    setTimeout(async () => {
      await performRetry();
    }, 500);
  }
  
  // Set up polling to check for pending requests every 1 second
  // This ensures requests are retried quickly even if online event is missed
  pollingInterval = setInterval(async () => {
    // Rely on performRetry internal lock
    if (isOnline()) {
      try {
        const pendingRequests = await getPendingRequests();
        if (pendingRequests && pendingRequests.length > 0) {
          await performRetry();
        }
      } catch (error) {
        console.error('Error checking pending requests:', error);
      }
    }
  }, 1000); // Check every 1 second for faster retry
  

  if (typeof window !== 'undefined') {
    window.__offlineRequestPollingInterval = pollingInterval;
  }
  
  // Register background sync when saving requests
  registerBackgroundSync();
};

/**
 * Setup global Axios interceptor for offline support
 * @param {Object} axiosInstance - The axios instance to intercept (usually the global axios)
 */
export const setupGlobalAxiosInterceptor = (axiosInstance) => {
  // Request interceptor to catch offline CUD operations and inject auth
  axiosInstance.interceptors.request.use(
    async (config) => {
      // 1. Automatic Header Injection (Token & Language)
      const token = getToken();
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (!config.headers['Accept-Language']) {
        config.headers['Accept-Language'] = getCurrentLanguage();
      }
      if (!config.headers['Accept']) {
        config.headers['Accept'] = 'application/json';
      }


      const method = (config.method || 'GET').toUpperCase();
      const online = isOnline();

      // 2. Proactive GET Cache Fallback (Avoid network timeout delay)
      if (!online && method === 'GET') {
        const cachedData = await getGenericResponseCache(config.url, config.params);
        if (cachedData) {
          const cacheError = new Error('CACHE_HIT');
          cacheError.mockResponse = {
            data: cachedData,
            status: 200, statusText: 'OK (Offline Cache)', config, isOfflineCache: true
          };
          return Promise.reject(cacheError);
        }
      }

      // 3. Offline CUD Queuing
      const isCudOperation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
      if (!online && isCudOperation) {
        if (config.url.includes('/login')) return config;

        try {
          const fullUrl = config.url.startsWith('http') 
            ? config.url 
            : `${config.baseURL || BASE_URL}${config.url.startsWith('/') ? '' : '/'}${config.url}`;
          
          await saveRequest({
            url: fullUrl,
            method: method,
            headers: config.headers,
            formData: config.data,
          });

          toast.info(
            getCurrentLanguage() === 'ar' 
              ? 'تم حفظ العملية محلياً وسيتم مزامنتها عند عودة الاتصال' 
              : 'Action saved locally and will sync when online',
            { toastId: 'offline-cud-saved', autoClose: 2000 }
          );

          let mockData = {};
          try {
            if (config.data instanceof FormData) {
              config.data.forEach((value, key) => {
                const cleanKey = key.replace('[]', '');
                if (key.includes('[]')) {
                  if (!mockData[cleanKey]) mockData[cleanKey] = [];
                  mockData[cleanKey].push(value);
                } else { mockData[key] = value; }
              });
            } else if (typeof config.data === 'string') {
              mockData = JSON.parse(config.data);
            } else { mockData = config.data || {}; }
          } catch (e) { mockData = config.data || {}; }

          const optimisticError = new Error('OPTIMISTIC_SUCCESS');
          optimisticError.mockResponse = {
            data: { success: true, message: 'Queued', data: mockData, ...mockData },
            status: 200, statusText: 'OK', config, isOfflineOptimistic: true
          };
          return Promise.reject(optimisticError);
        } catch (error) {
          if (error.mockResponse) return Promise.reject(error);
          return config;
        }
      }
      
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor to cache GET results and provide fallbacks
  axiosInstance.interceptors.response.use(
    (response) => {
      const method = (response.config.method || 'GET').toUpperCase();
      if (method === 'GET' && response.config.url) {
        saveGenericResponseCache(response.config.url, response.config.params, response.data).catch(() => {});
      }
      return response;
    },
    async (error) => {
      // Catch our custom short-circuit errors
      if ((error.message === 'OPTIMISTIC_SUCCESS' || error.message === 'CACHE_HIT') && error.mockResponse) {
        return error.mockResponse;
      }

      const config = error.config;
      if (!config) return Promise.reject(error);

      // Reactive Cache Fallback (If online check passed but network still failed)
      if ((config.method || 'GET').toUpperCase() === 'GET') {
        const cachedData = await getGenericResponseCache(config.url, config.params);
        if (cachedData) {
          return { 
            data: cachedData, 
            status: 200, statusText: 'OK (Offline Cache)', 
            headers: {}, config, isOfflineCache: true 
          };
        }
      }

      return Promise.reject(error);
    }
  );
};
